import { FC } from 'react';
import styled, { keyframes } from 'styled-components';
import QRCode from 'react-qr-code';
import { useTranslation } from 'react-i18next';
import { ReadablePalette } from '../common/Color';
import { u } from '../common/Units';
import CloseIcon from '../assets/icon_close.svg?react';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  animation: ${fadeIn} 0.2s;
`;

const Dialog = styled.div<{ $palette: ReadablePalette }>`
  position: relative;
  width: ${u(560)};
  max-width: calc(100% - ${u(80)});
  padding: ${u(50)} ${u(50)} ${u(40)};
  box-sizing: border-box;
  border-radius: ${u(24)};
  background-color: ${p => p.$palette.surfaceColor};
  color: ${p => p.$palette.textColor};
  text-align: center;
  box-shadow: 0 ${u(20)} ${u(60)} rgba(0, 0, 0, 0.4);

  & h2 {
    margin: 0 ${u(30)} ${u(28)};
    font-size: 1.2em;
    line-height: 1.3;
  }

  & .qr {
    display: inline-block;
    padding: ${u(16)};
    background: #fff;
    border-radius: ${u(12)};
    line-height: 0;
  }

  & .qr svg {
    width: ${u(260)};
    height: ${u(260)};
  }

  & p {
    margin: ${u(24)} 0 0;
    font-size: 0.85em;
    color: ${p => p.$palette.mutedTextColor};
    overflow-wrap: anywhere;
  }

  & button {
    position: absolute;
    top: ${u(16)};
    right: ${u(16)};
    width: ${u(56)};
    height: ${u(56)};
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${p => p.$palette.cardColor};
    color: inherit;
    cursor: pointer;
  }

  & button svg {
    width: ${u(20)};
    height: ${u(20)};
  }

  & button svg path:first-child {
    fill: currentColor;
  }
`;

export interface DMLink {
  url: string,
  label: string
}

interface DMLinkPopupProps {
  link: DMLink,
  palette: ReadablePalette,
  onClose: () => void
}

/**
 * Shows a QR code for an external link, since links can not be opened in the kiosk
 * @returns {JSX.Element} Component template
 */
const DMLinkPopup: FC<DMLinkPopupProps> = ({ link, palette, onClose }) => {
  const { t } = useTranslation();
  return (
    <Overlay onClick={onClose}>
      <Dialog $palette={palette} role="dialog" aria-modal={true} aria-label={link.label} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} aria-label={t("dm.close")}><CloseIcon/></button>
        <h2>{link.label}</h2>
        <div className="qr"><QRCode value={link.url} size={256}/></div>
        <p>{t("dm.link.scan")}<br/>{link.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}</p>
      </Dialog>
    </Overlay>
  );
}

export default DMLinkPopup;
